import { Component, Event, EventEmitter, h, State } from '@stencil/core';

import { createFocusTrap } from 'focus-trap';

import { Configuration } from '@entities/Configuration';
import { Design } from '@entities/Design';
import { FieldPayload } from '@entities/FieldPayload';
import { RatingScaleDesign } from '@entities/RatingScaleDesign';
import { ServiceType } from '@entities/ServiceType';
import { TransactionalServiceType } from '@entities/TransactionalServiceType';
import { WidgetPosition } from '@entities/WidgetPosition';
import appStore from '@stores/app.store';

enum SatisfactionState {
  Rating,
  Responded,
}

@Component({
  tag: 'wog-rating',
})
export class Rating {
  @State() ratedValue: number;
  @State() satisfactionState: SatisfactionState = SatisfactionState.Rating;
  @State() isFeedbackButtonActive = false;
  @Event() ratingClose: EventEmitter<void>;
  @Event() ratingSubmit: EventEmitter<{ value: number }>;
  @Event() ratingSubmitAnimationComplete: EventEmitter;

  private ratingContainer: HTMLDivElement;
  private ratingDummyContainer: HTMLDivElement;
  private trap = createFocusTrap('', {});

  componentDidLoad() {
    setTimeout(() => {
      this.isFeedbackButtonActive = true;
      this.trap = createFocusTrap(this.ratingContainer, {
        allowOutsideClick: true,
        clickOutsideDeactivates: true,
        escapeDeactivates: false,
        initialFocus: this.ratingDummyContainer,
        onPostActivate: () => {
          this.trapDeactivate();
          document.body.focus();
        },
      });
      this.trapActivate();
    }, 300);
  }

  private trapActivate = () => {
    if (!this.trap.active) {
      this.trap.activate();
    }
  };

  private trapDeactivate = () => {
    if (this.trap.active) {
      this.trap.deactivate();
    }
  };

  private onFeedbackSubmit = (event: CustomEvent<FieldPayload>) => {
    this.trapDeactivate();
    this.satisfactionState = SatisfactionState.Responded;
    const { values } = event.detail;
    this.ratedValue = parseInt(values[0]);
    this.ratingSubmit.emit({ value: this.ratedValue });
    this.ratingSubmitAnimationComplete.emit();
  };

  private onRatingClose = () => {
    this.ratingClose.emit();
  };

  private renderSvg() {
    return (
      <svg
        fill="none"
        class="wog--h-12 wog--w-12"
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg">
        <path
          clip-rule="evenodd"
          d="m32 64c17.6731 0 32-14.3269 32-32s-14.3269-32-32-32-32 14.3269-32 32 14.3269 32 32 32z"
          fill="#333"
          fill-rule="evenodd"
        />
        <path
          d="m55 32c0 12.6829-10.3171 23-23 23s-23-10.3171-23-23 10.3171-23 23-23 23 10.3171 23 23"
          fill="#fddc67"
        />
        <g fill="#333">
          <path d="m32.0001 45.3243c-5.0397 0-10.0104-2.002-13.8075-6.0062-.6214-.6904-.6214-1.726 0-2.4164.6213-.6904 1.6569-.6904 2.2782 0 6.3515 6.6277 16.7072 6.6967 23.0587 0 .6213-.6904 1.6569-.6904 2.2782 0 .6214.6904.6214 1.726 0 2.4164-3.7971 4.0042-8.8368 6.0062-13.8076 6.0062" />
          <path d="m22.6102 30.5502c-1.6569 0-2.9686-1.1736-2.9686-2.5544v-1.7259c0-1.4498 1.3117-2.5544 2.9686-2.5544s2.9687 1.1736 2.9687 2.5544v1.7259c0 1.3808-1.3118 2.5544-2.9687 2.5544" />
          <path d="m41.3895 30.5502c-1.6569 0-2.9686-1.1736-2.9686-2.5544v-1.7259c0-1.4498 1.3117-2.5544 2.9686-2.5544s2.9687 1.1736 2.9687 2.5544v1.7259c0 1.3808-1.3808 2.5544-2.9687 2.5544" />
        </g>
      </svg>
    );
  }

  private renderRatingField(config: Configuration) {
    if (config.ratingScaleDesign === RatingScaleDesign.Numerical) {
      return (
        <wog-linear-scale-field
          name={'stranded-linear-scale'}
          themeColor={config.themeColor}
          appearance={config.appearance}
          highScale={6}
          lowScale={1}
          isDisabled={!this.isFeedbackButtonActive}
          onValueSubmit={this.onFeedbackSubmit}
          customTabIndex={0}
        />
      );
    }

    if (config.ratingScaleDesign === RatingScaleDesign.Emoji) {
      return (
        <wog-emoji-rating-scale-field
          name={'stranded-linear-scale'}
          themeColor={config.themeColor}
          appearance={config.appearance}
          isDisabled={!this.isFeedbackButtonActive}
          onValueSubmit={this.onFeedbackSubmit}
          customTabIndex={0}
        />
      );
    }

    if (config.ratingScaleDesign === RatingScaleDesign.Star) {
      return (
        <wog-star-rating-scale-field
          name={'stranded-linear-scale'}
          themeColor={config.themeColor}
          appearance={config.appearance}
          highScale={6}
          lowScale={1}
          isDisabled={!this.isFeedbackButtonActive}
          onValueSubmit={this.onFeedbackSubmit}
          customTabIndex={0}
        />
      );
    }
  }

  private renderConditionalView(
    viewState: SatisfactionState,
    config: Configuration,
    ratingQuestion: string,
  ) {
    if (viewState === SatisfactionState.Rating) {
      const containerClasses = {
        'wog--box-border': true,
        'wog--w-full': true,
        'wog--px-2': true,
      };

      const ratingFieldClasses = {
        'wog--px-2':
          config.ratingScaleDesign === RatingScaleDesign.Emoji ||
          config.ratingScaleDesign === RatingScaleDesign.Star,
        'wog--px-4': config.ratingScaleDesign === RatingScaleDesign.Numerical,
      };

      return (
        <div
          class={containerClasses}
          tabIndex={0}
          ref={el => (this.ratingContainer = el as HTMLDivElement)}
          role="group">
          <wog-close-button onClose={this.onRatingClose} customAriaLabel={'Close rating popup'} />
          <div
            tabIndex={0}
            ref={el => (this.ratingDummyContainer = el as HTMLDivElement)}
            role="group"
            aria-roledescription="Sentiments widget popup"
            aria-labelledby="rating-heading"></div>
          <h4
            id={'rating-heading'}
            class="wog--text-foundation wog--base-title wog--satisfaction-animation wog--text-center wog--text-sm wog--text-theme-black"
            aria-label={ratingQuestion}>
            {ratingQuestion}
          </h4>
          <div class={ratingFieldClasses}>{this.renderRatingField(config)}</div>
        </div>
      );
    }
    if (config.design === Design.SmileyFace) {
      return (
        <div class="wog--flex wog--items-center wog--justify-center wog--py-6" role="alert">
          {this.renderSvg()}
          <h3 class="wog--text-foundation wog--base-title wog--m-0 wog--ml-3 wog--text-gray-800">
            Thank you! Tell us more.
          </h3>
        </div>
      );
    }
    return (
      <div role="alert">
        <h3 class="wog--text-foundation  wog--base-title wog--text-center wog--text-gray-800">
          Thank you! Tell us more.
        </h3>
      </div>
    );
  }

  private renderRatingQuestion = (
    serviceType: ServiceType,
    transactionalServiceType: TransactionalServiceType,
  ): string => {
    if (serviceType === ServiceType.InformationalService) {
      return 'Rate your experience with this website';
    } else {
      switch (transactionalServiceType) {
        case TransactionalServiceType.ApplicationOrRenewal:
          return 'How easy was it for you to complete this application process?';

        case TransactionalServiceType.Payment:
          return 'How easy was it for you to make this payment?';

        case TransactionalServiceType.BookingsOrAppointments:
          return 'How easy was it for you to book this appointment?';

        case TransactionalServiceType.InformationalService:
          return 'How easy was it to find what you need?';

        default:
          return 'Rate your experience with this transaction';
      }
    }
  };

  render() {
    const configuration = appStore.get('configuration');
    const serviceType = appStore.get('serviceType');
    const transactionalServiceType = appStore.get('transactionalServiceType');

    const elementClasses = {
      'wog--tab-view-container': true,
      'wog--flex': true,
      'wog--flex-col': true,
      'wog--items-center': true,
      'wog--justify-center': true,
      'wog--px-6': true,
      'wog--py-4': true,
      'wog--rating-custom': true,
      'wog--mobile-full-position': true,
      'wog--position-left-side': configuration.position === WidgetPosition.MiddleLeft,
      'wog--position-right-side': configuration.position === WidgetPosition.MiddleRight,
      'wog--position-bottom-left': configuration.position === WidgetPosition.BottomLeft,
      'wog--position-bottom-right': configuration.position === WidgetPosition.BottomRight,
    };

    const ratingQuestion = this.renderRatingQuestion(serviceType, transactionalServiceType);

    return (
      <div class={elementClasses}>
        {this.renderConditionalView(this.satisfactionState, configuration, ratingQuestion)}
      </div>
    );
  }
}
